import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { OrgLayout } from '../components/OrgLayout';
import { Modal } from '../components/Modal';
import { RecordInfoButton } from '../components/RecordInfoButton';
import { useToast } from '../components/Toast';
import type { ClassSectionGrid, DepartmentDto, Institution, ProgramDto } from '../types/models';

const emptyForm = { institutionId: '', departmentId: '', programId: '', code: '', name: '' };

export function ClassSectionPage() {
  const { orgId = '' } = useParams();
  const toast = useToast();

  const [insts, setInsts] = useState<Institution[]>([]);
  const [depts, setDepts] = useState<DepartmentDto[]>([]);
  const [programs, setPrograms] = useState<ProgramDto[]>([]);
  const [sections, setSections] = useState<ClassSectionGrid[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Filter bar (Institution → Department → Program).
  const [filter, setFilter] = useState({ institutionId: '', departmentId: '', programId: '' });

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function refresh() {
    try {
      const [i, d, p, s] = await Promise.all([
        api.listInstitutions(orgId), api.listDepartments(orgId),
        api.listPrograms(orgId), api.listAllClassSections(orgId),
      ]);
      setInsts(i); setDepts(d); setPrograms(p); setSections(s);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load class sections.');
    }
  }

  // Cascade inside the create/edit form.
  const deptsForForm = useMemo(
    () => depts.filter(d => d.institutionId === form.institutionId), [depts, form.institutionId]);
  const programsForForm = useMemo(
    () => programs.filter(p => p.departmentId === form.departmentId), [programs, form.departmentId]);

  const hasPrograms = programs.length > 0;

  // Filter-bar cascades + the filtered grid.
  const deptsForFilter = useMemo(
    () => depts.filter(d => d.institutionId === filter.institutionId), [depts, filter.institutionId]);
  const programsForFilter = useMemo(
    () => programs.filter(p => p.departmentId === filter.departmentId), [programs, filter.departmentId]);

  function setFilterField(key: keyof typeof filter, value: string) {
    setFilter(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') { next.departmentId = ''; next.programId = ''; }
      if (key === 'departmentId') { next.programId = ''; }
      return next;
    });
  }

  const filteredSections = useMemo(
    () => (sections ?? []).filter(s =>
      (!filter.institutionId || s.institutionId === filter.institutionId)
      && (!filter.departmentId || s.departmentId === filter.departmentId)
      && (!filter.programId || s.programId === filter.programId)),
    [sections, filter]);

  function setField(key: keyof typeof emptyForm, value: string) {
    setForm(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'institutionId') { next.departmentId = ''; next.programId = ''; }
      if (key === 'departmentId') { next.programId = ''; }
      return next;
    });
  }

  function openCreate() {
    setFormError(null); setEditId(null); setForm({ ...emptyForm }); setShowModal(true);
  }

  function openEdit(s: ClassSectionGrid) {
    setFormError(null);
    setEditId(s.id);
    setForm({
      institutionId: s.institutionId, departmentId: s.departmentId, programId: s.programId,
      code: s.code, name: s.name,
    });
    setShowModal(true);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    const req = { code: form.code.trim(), name: form.name.trim() };
    try {
      if (editId) {
        await api.updateClassSection(form.programId, editId, req);
      } else {
        await api.createClassSection(form.programId, req);
      }
      setShowModal(false);
      await refresh();
      toast.success(editId ? 'Class section updated.' : 'Class section created.');
    } catch (e: any) {
      setFormError(e?.message ?? 'Failed to save class section.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(s: ClassSectionGrid) {
    if (!confirm(`Delete class section “${s.name}”? This cannot be undone.`)) return;
    setError(null);
    try {
      await api.deleteClassSection(s.programId, s.id);
      await refresh();
      toast.success('Class section deleted.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to delete class section.';
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    <OrgLayout>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>Class Section</h1>
        <button className="primary" onClick={openCreate} disabled={!hasPrograms}>+ Add class section</button>
      </div>

      {!hasPrograms && sections && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="muted">
            Create an <Link to={`/org/${orgId}/institutions`}>institution</Link>,{' '}
            <Link to={`/org/${orgId}/departments`}>department</Link> and{' '}
            <Link to={`/org/${orgId}/programs`}>program</Link> first — class sections live under a program.
          </div>
        </div>
      )}

      {error && <div className="error" style={{ margin: '12px 0' }}>{error}</div>}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Institution</div>
            <select value={filter.institutionId} onChange={e => setFilterField('institutionId', e.target.value)}>
              <option value="">All</option>
              {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Department</div>
            <select value={filter.departmentId} disabled={!filter.institutionId}
                    onChange={e => setFilterField('departmentId', e.target.value)}>
              <option value="">All</option>
              {deptsForFilter.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 200px' }}>
            <div className="muted">Program</div>
            <select value={filter.programId} disabled={!filter.departmentId}
                    onChange={e => setFilterField('programId', e.target.value)}>
              <option value="">All</option>
              {programsForFilter.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
            </select>
          </label>
          {(filter.institutionId || filter.departmentId || filter.programId) && (
            <button onClick={() => setFilter({ institutionId: '', departmentId: '', programId: '' })}>Clear</button>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16, padding: 0, overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>S.no</th>
              <th>Institution</th>
              <th>Department</th>
              <th>Program</th>
              <th>Section Code</th>
              <th>Section Name</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!sections && <tr><td colSpan={7} className="muted">Loading…</td></tr>}
            {sections && filteredSections.length === 0 && (
              <tr><td colSpan={7} className="muted">
                {sections.length === 0 ? 'No class sections yet.' : 'No class sections match the selected filters.'}
              </td></tr>
            )}
            {filteredSections.map((s, idx) => (
              <tr key={s.id}>
                <td>{idx + 1}</td>
                <td>{s.institutionCode}</td>
                <td>{s.departmentCode}</td>
                <td>{s.programCode}</td>
                <td className="slug">{s.code}</td>
                <td style={{ fontWeight: 600 }}>{s.name}</td>
                <td>
                  <button className="link-btn" onClick={() => openEdit(s)}>Edit</button>
                  <button className="link-btn" style={{ color: 'var(--danger)' }} onClick={() => remove(s)}>Delete</button>
                  <RecordInfoButton entity="class-sections" id={s.id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit class section' : 'Create class section'} onClose={() => setShowModal(false)}>
          <form onSubmit={submit} className="col" style={{ marginTop: 16 }}>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div className="muted">Institution *</div>
                <select value={form.institutionId} required disabled={!!editId}
                        onChange={e => setField('institutionId', e.target.value)}>
                  <option value="" disabled>Select…</option>
                  {insts.map(i => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
                </select>
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Department *</div>
                <select value={form.departmentId} required disabled={!!editId || !form.institutionId}
                        onChange={e => setField('departmentId', e.target.value)}>
                  <option value="" disabled>{form.institutionId ? 'Select…' : 'Pick institution first'}</option>
                  {deptsForForm.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
                </select>
              </label>
            </div>
            <label>
              <div className="muted">Program *</div>
              <select value={form.programId} required disabled={!!editId || !form.departmentId}
                      onChange={e => setField('programId', e.target.value)}>
                <option value="" disabled>{form.departmentId ? 'Select…' : 'Pick department first'}</option>
                {programsForForm.map(p => <option key={p.id} value={p.id}>{p.code} — {p.name}</option>)}
              </select>
            </label>
            <div className="row" style={{ gap: 12 }}>
              <label style={{ flex: '0 0 160px' }}>
                <div className="muted">Section Code *</div>
                <input value={form.code} required maxLength={32}
                       onChange={e => setField('code', e.target.value)} placeholder="A" />
              </label>
              <label style={{ flex: 1 }}>
                <div className="muted">Section Name *</div>
                <input value={form.name} required maxLength={200}
                       onChange={e => setField('name', e.target.value)} placeholder="Section A" />
              </label>
            </div>
            {formError && <div className="error">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="primary" disabled={busy}>
                {busy ? 'Saving…' : editId ? 'Save changes' : 'Create class section'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </OrgLayout>
  );
}
